import { Component, Injector, Input, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatIconRegistry } from '@angular/material/icon';
import { Router, NavigationEnd } from '@angular/router';
import { DataService } from 'src/app/services/data.service';
import { IModules, IService, ISubModule } from 'src/app/model/http/menu.model';
import { Location } from '@angular/common';
import { ACTIONS_ADMIN, DEFAULT_ACTIONS } from 'src/app/constants/default-actions';
import { UTILS } from 'src/app/utils/methods/Utils';
import { MAT_MENU_DEFAULT_OPTIONS } from '@angular/material/menu';
import { UsersService } from 'src/app/services/users.service';
import { initialIdRoles } from 'src/app/pages/catalogs/roles/create-rol/constants/constants';
import { iconMenu } from 'src/app/constants/side-menu';
import { SafePipe } from 'src/app/pipes/safe/safe.pipe';


@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
  providers: [
    {
      provide: MAT_MENU_DEFAULT_OPTIONS,
      useValue: { overlayPanelClass: 'menu-collapse-overlay-pane' }
    }
  ]
})
export class SideMenuComponent implements OnInit, OnDestroy {
  @Input() menuUser: IModules[] = [];
  treeControl = new NestedTreeControl<any>(node => node.subModule);
  moduleSelect: string = '';
  subModuleSelect: string = '';
  serviceSelect: string = '';
  moduleClick: string = '';
  idModuleSelect: number = 0;
  screenWidth = window.innerWidth;
  moduleSelectActive: boolean = false;
  rolsActions: string = ''
  isAdmin: boolean = false;
  private readonly subscriptions = new Subscription();
  //injectors
  private readonly location: Location;

  constructor(
    private readonly matIcon: MatIconRegistry,
    private readonly router: Router,
    private readonly dataService: DataService,
    private readonly injector: Injector,
    private readonly userService: UsersService,
    private readonly safePipe: SafePipe
  ) {

    this.location = this.injector.get(Location);

    this.setupModulsIcons();
  }

  hasChild = (_: number, node: any) => !!node.subModule && node.subModule.length > 0;

  ngOnInit(): void {
    this.subscriptions.add(
      this.dataService.getScreenWidth$().subscribe(res => this.screenWidth = res)
    );

    for (const item of this.dataService.dataUser.rols) {
      this.rolsActions += `&rols=${item.id}`;
    }

    this.isAdmin = this.dataService.userHasRole(initialIdRoles.AdminGral);

    this.redirectModule();
    this.observableChangeRoute();
  }

  observableChangeRoute() {
    this.subscriptions.add(
      this.router.events.subscribe((ev: any) => {
        if (ev instanceof NavigationEnd) {
          this.findActionsByPath(ev.urlAfterRedirects);
        }
      })
    );
  }

  findActionsByPath(path: string): void {
    const bestMatch = this.getBestMatch(path);

    if (bestMatch) {
      this.treeControl.expand(bestMatch.module);
      this.setModuleSelect(bestMatch.route);
      this.checkActionsPermissions(bestMatch.serviceId);
    }
  }

  private getBestMatch(path: string): { module: IModules; serviceId: number; route: string } | null {
    let bestMatch: { module: IModules; serviceId: number; route: string } | null = null;

    for (const module of this.menuUser) {
      for (const submodule of module.subModule) {
        bestMatch = this.checkServiceRoutes(submodule, module, path, bestMatch);
        bestMatch = this.checkSubmoduleRoute(submodule, module, path, bestMatch);
      }
    }

    return bestMatch;
  }

  private checkServiceRoutes(
    submodule: ISubModule,
    module: IModules,
    path: string,
    bestMatch: { module: IModules; serviceId: number; route: string } | null
  ) {
    let match = bestMatch;
    for (const service of submodule.services) {
      if (service.route && path.includes(service.route)) {
        if (!match || service.route.length > match.route.length) {
          match = { module, serviceId: service.id, route: service.route };
        }
      }
    }
    return match;
  }

  private checkSubmoduleRoute(
    submodule: ISubModule,
    module: IModules,
    path: string,
    bestMatch: { module: IModules; serviceId: number; route: string } | null
  ) {
    let match = bestMatch;
    if (submodule.notServices && submodule.route && path.includes(submodule.route)) {
      if (!match || submodule.route.length > match.route.length) {
        match = { module, serviceId: submodule.id, route: submodule.route };
      }
    }
    return match;
  }

  checkActionsPermissions(serviceId: number) {

    if (this.isAdmin) {
      this.dataService.setActionsService$(ACTIONS_ADMIN);
    } else {
      this.userService.getServiceActions(serviceId, this.rolsActions).subscribe((res) => {
        this.dataService.setActionsService$(this.returnActionsComplete(res));
      });
    }

  }

  private setupModulsIcons(): void {

    for (const icon of iconMenu) {
      // Sonar ignore: This is a safe usage as icons are loaded from a controlled directory
      this.matIcon.addSvgIcon(icon, this.safePipe.transform(`assets/img/icons/${icon}.svg`, 'resourceUrl'));
    }

  }

  iconModule(route: string = '') {
    const optionsIcons: { [key: string]: any } = {
      users: 'icon_users',
      catalogs: 'icon_catalogs',
      libraries: 'icon_libraries',
      application: 'icon_application',
      communications: 'icon_communications',
      settings: 'icon_settings',
      file: 'icon_folder',
      calendar: 'icon_calendar',
      notifications: 'icon_notification',
      templates: 'icon_text',
      undefined: 'icon_default'
    }

    let data

    if (route) {
      data = this.getRouteOfSubmodules(route).route;
    }

    return optionsIcons[data] || 'icon_default';
  }

  redirectModule() {
    let locationPath = this.location.path();

    if (locationPath === '/' || locationPath === '') {
      let redirectRoute = '';

      if (this.menuUser) {
        let firtsSubModule = this.menuUser[0].subModule[0];
        redirectRoute = firtsSubModule.services.length > 0 ? firtsSubModule.services[0].route : firtsSubModule.route;
      }

      this.router.navigate([redirectRoute]);
      locationPath = redirectRoute;
    }

    this.findActionsByPath(locationPath)

  }

  openModule(subModule: ISubModule[]) {
    this.moduleClick = this.getRouteOfSubmodules(subModule[0].route).route;
  }

  redirectSubModule(subModule: ISubModule) {
    if (subModule.notServices) {
      this.setServiceSelect(subModule, subModule.services[0]);
    }
  }

  setModuleSelect(route: string) {
    let moduleRoute = this.getRouteOfSubmodules(route);

    this.moduleSelect = moduleRoute.route;
    this.moduleClick = moduleRoute.route;
    this.moduleSelectActive = true;

    if (moduleRoute.length > 2)
      this.subModuleSelect = this.getRouteWithSubmodule(route);

  }

  setServicesActions(id: number) {
    if (this.idModuleSelect === 0) {
      this.idModuleSelect = id;

      for (const module of this.menuUser) {
        if (module.id === this.idModuleSelect) {
          this.findSubModule(module);
          break;
        }
      }

    }
  }

  findSubModule(module: IModules) {
    for (const subModule of module.subModule) {
      if (subModule.route === this.subModuleSelect) {
        this.findServices(subModule);
        break;
      }
    }
  }

  findServices(subModule: ISubModule) {
    for (const service of subModule.services) {
      if (this.serviceSelect.includes(service.route)) {
        this.dataService.setActionsService$(this.returnActionsComplete(service.actions));
        break;
      }
    }
  }

  setServiceSelect(subModule: ISubModule, service: IService) {

    if (service.route.startsWith('http') || service.route.startsWith('https')) {
      window.open(service.route, '_blank');
      return;
    }

    subModule.status = false;

    this.serviceSelect = service.route;

    this.router.navigate([service.route]);
  }

  returnActionsComplete(actions: any) {
    let catalogActions: any = UTILS.clonObject(DEFAULT_ACTIONS)

    for (const item of actions) {
       catalogActions[item.description] = true;
    }

    return catalogActions;
  }

  getRouteOfSubmodules(subModule: string = '') {
    let splitRoute = subModule.split('/');
    return {
      route: splitRoute[1],
      length: splitRoute.length
    };
  }

  getRouteWithSubmodule(route: string = '') {
    let splitRoute = route.split('/');

    if (splitRoute.length > 3)
      this.serviceSelect = route;

    return `/${splitRoute[1]}/${splitRoute[2]}`
  }

  getCssModuleSelected(module: IModules) {

    if (!module?.subModule[0]?.route) {
      return '';
    }

    let moduleRoute = this.getRouteOfSubmodules(module.subModule[0].route).route;
    let classCss = '';

    if (this.moduleSelect === moduleRoute) {
      classCss = 'btn-menu-selected';
    }

    if (this.moduleClick === moduleRoute && this.moduleSelect !== this.moduleClick)
      classCss = 'btn-menu-click';

    return classCss;
  }

  setAllStatusSubModules() {

    for (const module of this.menuUser) {

      for (const sub of module.subModule) {
        sub.status = false;
      }

    }

  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}

